import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Landmark, Plus, Trash2, TrendingUp, TrendingDown, ArrowRight,
  Building2, Wallet, Search, X, Pencil, ArrowRightLeft, Banknote, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FunderPage = () => {
  const { getAuthHeaders } = useAuth();
  const { selectedFY } = useFinancialYear();

  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create/Edit loan modal
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null); // null = create, object = edit
  const [loanForm, setLoanForm] = useState({ funder_name: '', total_amount: '', online_amount: '', deposit_amount: '', loan_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [loanToDelete, setLoanToDelete] = useState(null);

  // Drill-down
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillAccount, setDrillAccount] = useState('');
  const [drillTransactions, setDrillTransactions] = useState([]);
  const [drillSearch, setDrillSearch] = useState('');
  const [drillLoading, setDrillLoading] = useState(false);

  // Transfer dialog
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Repayment dialog
  const [repayOpen, setRepayOpen] = useState(false);
  const [repayAmount, setRepayAmount] = useState('');
  const [repayDate, setRepayDate] = useState(new Date().toISOString().split('T')[0]);
  const [repayNote, setRepayNote] = useState('');
  const [repaying, setRepaying] = useState(false);
  const [editingRepayment, setEditingRepayment] = useState(null);

  // Edit transfer dialog
  const [editTransferOpen, setEditTransferOpen] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [editTransferAmount, setEditTransferAmount] = useState('');
  const [editTransferNote, setEditTransferNote] = useState('');
  const [editTransferSaving, setEditTransferSaving] = useState(false);

  // Delete confirmation for repayment/transfer
  const [deleteRepayOpen, setDeleteRepayOpen] = useState(false);
  const [repayToDelete, setRepayToDelete] = useState(null);
  const [deleteTransferOpen, setDeleteTransferOpen] = useState(false);
  const [transferToDelete, setTransferToDelete] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [loansResp, summaryResp] = await Promise.all([
        axios.get(`${API}/funder/loans?financial_year=${selectedFY}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/funder/summary?financial_year=${selectedFY}`, { headers: getAuthHeaders() })
      ]);
      setLoans(loansResp.data.loans || []);
      setSummary(summaryResp.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch funder data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedFY]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchTransactions = useCallback(async (accountType, search) => {
    try {
      setDrillLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedFY) params.set('financial_year', selectedFY);
      const resp = await axios.get(`${API}/funder/transactions/${accountType}?${params}`, { headers: getAuthHeaders() });
      setDrillTransactions(resp.data.transactions || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch transactions:', error);
    } finally {
      setDrillLoading(false);
    }
  }, [getAuthHeaders, selectedFY]);

  const openDrillDown = (accountType) => {
    setDrillAccount(accountType);
    setDrillSearch('');
    setDrillOpen(true);
    fetchTransactions(accountType, '');
  };

  useEffect(() => {
    if (drillOpen && drillAccount) {
      const timer = setTimeout(() => fetchTransactions(drillAccount, drillSearch), 300);
      return () => clearTimeout(timer);
    }
  }, [drillSearch, drillOpen, drillAccount, fetchTransactions]);

  // Loan create/edit
  const openCreateLoan = () => {
    setEditingLoan(null);
    setLoanForm({ funder_name: '', total_amount: '', online_amount: '', deposit_amount: '', loan_date: new Date().toISOString().split('T')[0], notes: '' });
    setLoanDialogOpen(true);
  };

  const openEditLoan = (loan) => {
    setEditingLoan(loan);
    setLoanForm({
      funder_name: loan.funder_name,
      total_amount: String(loan.total_amount),
      online_amount: String(loan.online_amount),
      deposit_amount: String(loan.deposit_amount),
      loan_date: loan.loan_date,
      notes: loan.notes || ''
    });
    setLoanDialogOpen(true);
  };

  const handleSaveLoan = async () => {
    const total = parseFloat(loanForm.total_amount) || 0;
    const online = parseFloat(loanForm.online_amount) || 0;
    const deposit = parseFloat(loanForm.deposit_amount) || 0;
    if (!loanForm.funder_name.trim()) { toast.error('Funder name is required'); return; }
    if (total <= 0) { toast.error('Total must be > 0'); return; }
    if (Math.abs(online + deposit - total) > 0.01) { toast.error('Online + Deposit must equal Total'); return; }

    try {
      setSaving(true);
      const payload = {
        funder_name: loanForm.funder_name, total_amount: total, online_amount: online,
        deposit_amount: deposit, loan_date: loanForm.loan_date, financial_year: selectedFY, notes: loanForm.notes
      };
      if (editingLoan) {
        await axios.put(`${API}/funder/loans/${editingLoan.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Loan updated');
      } else {
        await axios.post(`${API}/funder/loans`, payload, { headers: getAuthHeaders() });
        toast.success('Loan recorded');
      }
      setLoanDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!loanToDelete) return;
    try {
      await axios.delete(`${API}/funder/loans/${loanToDelete.id}`, { headers: getAuthHeaders() });
      toast.success('Loan deleted');
      setDeleteOpen(false); setLoanToDelete(null); fetchData();
    } catch (error) { toast.error('Failed to delete'); }
  };

  // Transfer
  const handleTransfer = async () => {
    const amt = parseFloat(transferAmount) || 0;
    if (amt <= 0) { toast.error('Amount must be > 0'); return; }
    try {
      setTransferring(true);
      await axios.post(`${API}/funder/transfer-to-deposit?financial_year=${selectedFY}`, { amount: amt, note: transferNote }, { headers: getAuthHeaders() });
      toast.success(`Transferred ${formatCurrency(amt)} to Deposit`);
      setTransferOpen(false); setTransferAmount(''); setTransferNote(''); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally { setTransferring(false); }
  };

  // Repayment
  const handleRepay = async () => {
    const amt = parseFloat(repayAmount) || 0;
    if (amt <= 0) { toast.error('Amount must be > 0'); return; }
    try {
      setRepaying(true);
      if (editingRepayment) {
        await axios.put(`${API}/funder/repayments/${editingRepayment.id}`, { amount: amt, repayment_date: repayDate, note: repayNote }, { headers: getAuthHeaders() });
        toast.success('Repayment updated');
      } else {
        await axios.post(`${API}/funder/repayment?financial_year=${selectedFY}`, { amount: amt, repayment_date: repayDate, note: repayNote }, { headers: getAuthHeaders() });
        toast.success(`Repayment of ${formatCurrency(amt)} recorded`);
      }
      setRepayOpen(false); setRepayAmount(''); setRepayNote(''); setEditingRepayment(null); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Repayment failed');
    } finally { setRepaying(false); }
  };

  const openEditRepayment = (r) => {
    setEditingRepayment(r);
    setRepayAmount(String(r.amount));
    setRepayDate(r.repayment_date || new Date().toISOString().split('T')[0]);
    setRepayNote(r.note || '');
    setRepayOpen(true);
  };

  const handleDeleteRepayment = async () => {
    if (!repayToDelete) return;
    try {
      await axios.delete(`${API}/funder/repayments/${repayToDelete.id}`, { headers: getAuthHeaders() });
      toast.success('Repayment deleted');
      setDeleteRepayOpen(false); setRepayToDelete(null); fetchData();
    } catch (error) { toast.error('Failed to delete repayment'); }
  };

  // Edit Transfer
  const openEditTransfer = (t) => {
    setEditingTransfer(t);
    setEditTransferAmount(String(t.amount));
    setEditTransferNote(t.note || '');
    setEditTransferOpen(true);
  };

  const handleEditTransfer = async () => {
    const amt = parseFloat(editTransferAmount) || 0;
    if (amt <= 0) { toast.error('Amount must be > 0'); return; }
    try {
      setEditTransferSaving(true);
      await axios.put(`${API}/funder/transfers/${editingTransfer.id}`, { amount: amt, note: editTransferNote }, { headers: getAuthHeaders() });
      toast.success('Transfer updated');
      setEditTransferOpen(false); setEditingTransfer(null); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transfer');
    } finally { setEditTransferSaving(false); }
  };

  const handleDeleteTransfer = async () => {
    if (!transferToDelete) return;
    try {
      await axios.delete(`${API}/funder/transfers/${transferToDelete.id}`, { headers: getAuthHeaders() });
      toast.success('Transfer deleted');
      setDeleteTransferOpen(false); setTransferToDelete(null); fetchData();
    } catch (error) { toast.error('Failed to delete transfer'); }
  };

  const handleOnlineChange = (val) => {
    const online = parseFloat(val) || 0;
    const total = parseFloat(loanForm.total_amount) || 0;
    setLoanForm(prev => ({ ...prev, online_amount: val, deposit_amount: String(Math.max(0, total - online)) }));
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate().toString().padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
  };

  const onlineSurplus = summary?.online_surplus || 0;
  const effectiveLoan = summary?.effective_loan || 0;
  const outstanding = (summary?.net_position || 0) - (summary?.funded?.total || 0);

  return (
    <div className="space-y-6" data-testid="funder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950 flex items-center gap-3"><Landmark className="w-7 h-7" />Funder Loans</h1>
          <p className="text-slate-500 text-sm mt-1">Track funder capital and reconcile against expenses & revenue</p>
        </div>
        <div className="flex gap-2">
          {onlineSurplus > 0 && (
            <Button variant="outline" size="sm" onClick={() => { setTransferAmount(''); setTransferNote(''); setTransferOpen(true); }} data-testid="transfer-btn">
              <ArrowRightLeft className="w-4 h-4 mr-2" />Transfer to Deposit
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => { setRepayAmount(''); setRepayNote(''); setRepayDate(new Date().toISOString().split('T')[0]); setEditingRepayment(null); setRepayOpen(true); }} data-testid="repay-btn">
            <Banknote className="w-4 h-4 mr-2" />Payout Loan
          </Button>
          <Button size="sm" className="bg-indigo-600" onClick={openCreateLoan} data-testid="add-loan-btn">
            <Plus className="w-4 h-4 mr-2" />Record Loan
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-400">Showing data for FY {selectedFY}</p>

      {/* Reconciliation Summary */}
      {summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Online Card */}
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('ONLINE')} data-testid="online-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-blue-600 text-white"><Building2 className="w-4 h-4" /></div>
                  <span className="font-semibold text-blue-900">Online</span>
                  <span className="ml-auto text-xs text-blue-400">Click for details</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-blue-600">Funded</span><span className="font-mono font-medium text-blue-800">{formatCurrency(summary.funded?.online)}</span></div>
                  <div className="flex justify-between"><span className="text-red-500 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Spent</span><span className="font-mono text-red-600">-{formatCurrency(summary.spent?.online)}</span></div>
                  <div className="flex justify-between"><span className="text-emerald-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Revenue
                    <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-slate-400 cursor-help" /></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p className="font-semibold mb-1">Revenue = Invoice Total – GST – TDS</p><p className="text-slate-300">e.g. Invoice ₹1,18,000 – GST ₹18,000 – TDS ₹10,000 = <span className="font-mono font-semibold">₹90,000</span> revenue (bank receives ₹1,08,000)</p></TooltipContent></Tooltip></TooltipProvider>
                  </span><span className="font-mono text-emerald-600">+{formatCurrency(summary.revenue?.online)}</span></div>
                  {(summary.total_transferred || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-amber-500">Transferred Out</span><span className="font-mono text-amber-600">-{formatCurrency(summary.total_transferred)}</span></div>
                  )}
                  <div className="flex justify-between border-t border-blue-200 pt-2 font-semibold"><span className="text-blue-900">Balance</span><span className={`font-mono ${(summary.balance?.online || 0) < 0 ? 'text-red-600' : 'text-blue-900'}`}>{formatCurrency(summary.balance?.online)}</span></div>
                </div>
              </CardContent>
            </Card>

            {/* Deposit Card */}
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('DEPOSIT')} data-testid="deposit-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-emerald-600 text-white"><Wallet className="w-4 h-4" /></div>
                  <span className="font-semibold text-emerald-900">Deposit</span>
                  <span className="ml-auto text-xs text-emerald-400">Click for details</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-emerald-600">Funded</span><span className="font-mono font-medium text-emerald-800">{formatCurrency(summary.funded?.deposit)}</span></div>
                  <div className="flex justify-between"><span className="text-red-500 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Spent</span><span className="font-mono text-red-600">-{formatCurrency(summary.spent?.deposit)}</span></div>
                  {(summary.total_transferred || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-blue-500">Transferred In</span><span className="font-mono text-blue-600">+{formatCurrency(summary.total_transferred)}</span></div>
                  )}
                  <div className="flex justify-between border-t border-emerald-200 pt-2 font-semibold"><span className="text-emerald-900">Balance</span><span className={`font-mono ${(summary.balance?.deposit || 0) < 0 ? 'text-red-600' : 'text-emerald-900'}`}>{formatCurrency(summary.balance?.deposit)}</span></div>
                </div>
              </CardContent>
            </Card>

            {/* Funder Position */}
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="funder-position-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-indigo-600 text-white"><Landmark className="w-4 h-4" /></div>
                  <span className="font-semibold text-indigo-900">Funder Position</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-indigo-600">Total Loan</span><span className="font-mono font-medium text-indigo-800">{formatCurrency(summary.funded?.total)}</span></div>
                  {(summary.total_repaid || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-purple-500">Repaid</span><span className="font-mono text-purple-600">-{formatCurrency(summary.total_repaid)}</span></div>
                  )}
                  {(summary.total_repaid || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-indigo-500">Effective Loan</span><span className="font-mono text-indigo-700">{formatCurrency(effectiveLoan)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-red-500">Total Spent</span><span className="font-mono text-red-600">-{formatCurrency(summary.spent?.total)}</span></div>
                  <div className="flex justify-between"><span className="text-emerald-500 flex items-center gap-1">Revenue Collected
                    <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-slate-400 cursor-help" /></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p className="font-semibold mb-1">Revenue = Invoice Total – GST – TDS</p><p className="text-slate-300">e.g. Invoice ₹1,18,000 – GST ₹18,000 – TDS ₹10,000 = <span className="font-mono font-semibold">₹90,000</span> revenue (bank receives ₹1,08,000)</p></TooltipContent></Tooltip></TooltipProvider>
                  </span><span className="font-mono text-emerald-600">+{formatCurrency(summary.revenue?.total)}</span></div>
                  <div className="flex justify-between border-t border-indigo-200 pt-2 font-semibold">
                    <span className="text-indigo-900">Net Position</span>
                    <span className={`font-mono ${(summary.net_position || 0) < 0 ? 'text-red-600' : 'text-indigo-900'}`}>{formatCurrency(summary.net_position)}</span>
                  </div>
                  <p className="text-xs text-slate-400">Loan – Spent + Revenue</p>
                  <div className="flex justify-between pt-1">
                    <span className="text-amber-600 font-medium">Outstanding</span>
                    <span className={`font-mono font-medium ${outstanding < 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{formatCurrency(outstanding)}</span>
                  </div>
                  <p className="text-xs text-slate-400">Net Position – Total Loan</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Flow Diagram */}
          {summary.funded?.total > 0 && (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
                  <div className="text-center"><p className="text-xs text-slate-500 mb-1">{effectiveLoan < summary.funded?.total ? 'Effective Loan' : 'Funder Loan'}</p><p className="font-mono font-bold text-lg text-indigo-700">{formatCurrency(effectiveLoan)}</p></div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                  <div className="text-center"><p className="text-xs text-slate-500 mb-1">Expenses Paid</p><p className="font-mono font-bold text-lg text-red-600">-{formatCurrency(summary.spent?.total)}</p></div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                  <div className="text-center"><p className="text-xs text-slate-500 mb-1 flex items-center justify-center gap-1">Revenue In
                    <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-slate-400 cursor-help" /></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-xs"><p className="font-semibold mb-1">Revenue = Invoice Total – GST – TDS</p><p className="text-slate-300">Not the bank receipt. GST & TDS are excluded.</p></TooltipContent></Tooltip></TooltipProvider>
                  </p><p className="font-mono font-bold text-lg text-emerald-600">+{formatCurrency(summary.revenue?.total)}</p></div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                  <div className="text-center"><p className="text-xs text-slate-500 mb-1">Net Position</p><p className={`font-mono font-bold text-lg ${(summary.net_position || 0) < 0 ? 'text-red-600' : 'text-indigo-700'}`}>{formatCurrency(summary.net_position)}</p></div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Loan Records */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-indigo-950">Loan Records</CardTitle>
          <CardDescription>FY {selectedFY} — Click pencil icon to edit</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-14 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : loans.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <Landmark className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p>No funder loans for FY {selectedFY}.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 font-semibold text-slate-600">Funder</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Total</th>
                  <th className="text-right p-3 font-semibold text-blue-600">Online</th>
                  <th className="text-right p-3 font-semibold text-emerald-600">Deposit</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Notes</th>
                  <th className="text-center p-3 font-semibold text-slate-600">Actions</th>
                </tr></thead>
                <tbody>
                  {loans.map((loan) => (
                    <tr key={loan.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-medium">{loan.funder_name}</td>
                      <td className="p-3 text-slate-500">{formatDate(loan.loan_date)}</td>
                      <td className="p-3 text-right font-mono font-semibold">{formatCurrency(loan.total_amount)}</td>
                      <td className="p-3 text-right font-mono text-blue-600">{formatCurrency(loan.online_amount)}</td>
                      <td className="p-3 text-right font-mono text-emerald-600">{formatCurrency(loan.deposit_amount)}</td>
                      <td className="p-3 text-xs text-slate-500 max-w-[200px] truncate">{loan.notes || '-'}</td>
                      <td className="p-3 text-center flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" className="text-indigo-500 hover:text-indigo-700 h-7" onClick={() => openEditLoan(loan)} data-testid={`edit-loan-${loan.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7" onClick={() => { setLoanToDelete(loan); setDeleteOpen(true); }} data-testid={`delete-loan-${loan.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {loans.length > 1 && (
                  <tfoot className="bg-slate-100"><tr>
                    <td colSpan="2" className="p-3 font-semibold">Total</td>
                    <td className="p-3 text-right font-mono font-bold">{formatCurrency(loans.reduce((s,l) => s + (l.total_amount || 0), 0))}</td>
                    <td className="p-3 text-right font-mono font-bold text-blue-600">{formatCurrency(loans.reduce((s,l) => s + (l.online_amount || 0), 0))}</td>
                    <td className="p-3 text-right font-mono font-bold text-emerald-600">{formatCurrency(loans.reduce((s,l) => s + (l.deposit_amount || 0), 0))}</td>
                    <td colSpan="2"></td>
                  </tr></tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Repayment History */}
      {summary?.repayments?.length > 0 && (
        <Card className="border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading text-purple-900">Loan Repayments</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                <th className="text-right p-3 font-semibold text-slate-600">Amount</th>
                <th className="text-left p-3 font-semibold text-slate-600">Note</th>
                <th className="text-left p-3 font-semibold text-slate-600">By</th>
                <th className="text-center p-3 font-semibold text-slate-600">Actions</th>
              </tr></thead>
              <tbody>
                {summary.repayments.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-slate-100">
                    <td className="p-3 text-slate-500">{formatDate(r.repayment_date)}</td>
                    <td className="p-3 text-right font-mono font-medium text-purple-600">{formatCurrency(r.amount)}</td>
                    <td className="p-3 text-xs text-slate-500">{r.note || '-'}</td>
                    <td className="p-3 text-xs text-slate-500">{r.created_by}</td>
                    <td className="p-3 text-center flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" className="text-indigo-500 hover:text-indigo-700 h-7" onClick={() => openEditRepayment(r)} data-testid={`edit-repay-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7" onClick={() => { setRepayToDelete(r); setDeleteRepayOpen(true); }} data-testid={`delete-repay-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50"><tr>
                <td className="p-3 font-semibold">Total Repaid</td>
                <td className="p-3 text-right font-mono font-bold text-purple-600">{formatCurrency(summary.total_repaid)}</td>
                <td colSpan="3"></td>
              </tr></tfoot>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Transfer History */}
      {summary?.transfers?.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading text-amber-900">Fund Transfers (Online to Deposit)</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                <th className="text-right p-3 font-semibold text-slate-600">Amount</th>
                <th className="text-left p-3 font-semibold text-slate-600">Note</th>
                <th className="text-left p-3 font-semibold text-slate-600">By</th>
                <th className="text-center p-3 font-semibold text-slate-600">Actions</th>
              </tr></thead>
              <tbody>
                {summary.transfers.map((t, i) => (
                  <tr key={t.id || i} className="border-b border-slate-100">
                    <td className="p-3 text-slate-500">{formatDate(t.created_at?.split('T')[0])}</td>
                    <td className="p-3 text-right font-mono font-medium text-amber-600">{formatCurrency(t.amount)}</td>
                    <td className="p-3 text-xs text-slate-500">{t.note || '-'}</td>
                    <td className="p-3 text-xs text-slate-500">{t.created_by}</td>
                    <td className="p-3 text-center flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" className="text-indigo-500 hover:text-indigo-700 h-7" onClick={() => openEditTransfer(t)} data-testid={`edit-transfer-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7" onClick={() => { setTransferToDelete(t); setDeleteTransferOpen(true); }} data-testid={`delete-transfer-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50"><tr>
                <td className="p-3 font-semibold">Total Transferred</td>
                <td className="p-3 text-right font-mono font-bold text-amber-600">{formatCurrency(summary.total_transferred)}</td>
                <td colSpan="3"></td>
              </tr></tfoot>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Drill-down Dialog */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drillAccount === 'ONLINE' ? <Building2 className="w-5 h-5 text-blue-600" /> : <Wallet className="w-5 h-5 text-emerald-600" />}
              {drillAccount} — Paid Expenses
            </DialogTitle>
            <DialogDescription>Expenses paid via {drillAccount === 'ONLINE' ? 'Online / Omnicard / UPI' : 'Deposit'} in FY {selectedFY}</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by name, project ID, vendor..." value={drillSearch} onChange={(e) => setDrillSearch(e.target.value)} className="pl-9" data-testid="drill-search" />
            {drillSearch && <button onClick={() => setDrillSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
          </div>
          <div className="overflow-y-auto max-h-[50vh]">
            {drillLoading ? (
              <div className="space-y-2 p-4">{[1,2,3].map(i => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
            ) : drillTransactions.length === 0 ? (
              <div className="text-center py-8 text-slate-500"><p>No expenses found{drillSearch ? ` for "${drillSearch}"` : ''}.</p></div>
            ) : (
              <>
                <div className="text-xs text-slate-400 px-2 pb-2">{drillTransactions.length} expense(s) — Total: {formatCurrency(drillTransactions.reduce((s, t) => s + (t.amount || 0), 0))}</div>
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white"><tr className="border-b border-slate-200">
                    <th className="text-left p-2 text-xs font-semibold text-slate-500">Name</th>
                    <th className="text-left p-2 text-xs font-semibold text-slate-500">Type</th>
                    <th className="text-left p-2 text-xs font-semibold text-slate-500">Project</th>
                    <th className="text-left p-2 text-xs font-semibold text-slate-500">Mode</th>
                    <th className="text-right p-2 text-xs font-semibold text-slate-500">Amount</th>
                    <th className="text-left p-2 text-xs font-semibold text-slate-500">Date</th>
                  </tr></thead>
                  <tbody>
                    {drillTransactions.map((txn, idx) => (
                      <tr key={txn.expense_id || `txn-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-2 text-sm font-medium">{txn.name}</td>
                        <td className="p-2"><Badge className={`text-xs ${txn.expense_type === 'CORPORATE_TRAINING' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{txn.expense_type === 'CORPORATE_TRAINING' ? 'Training' : 'HR'}</Badge></td>
                        <td className="p-2 font-mono text-xs text-slate-500">{txn.project_id || '-'}</td>
                        <td className="p-2 text-xs">{txn.mode || '-'}</td>
                        <td className="p-2 text-right font-mono font-medium text-red-600">{formatCurrency(txn.amount)}</td>
                        <td className="p-2 text-xs text-slate-500">{formatDate(txn.date?.split('T')[0])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Loan Dialog */}
      <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLoan ? 'Edit Loan' : 'Record Funder Loan'}</DialogTitle>
            <DialogDescription>Enter loan details and distribution</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Funder Name</Label><Input value={loanForm.funder_name} onChange={(e) => setLoanForm({ ...loanForm, funder_name: e.target.value })} placeholder="e.g., ABC Capital" className="mt-1" data-testid="loan-funder-name" /></div>
            <div><Label>Loan Date</Label><Input type="date" value={loanForm.loan_date} onChange={(e) => setLoanForm({ ...loanForm, loan_date: e.target.value })} className="mt-1" data-testid="loan-date" /></div>
            <div><Label>Total Loan Amount</Label><Input type="number" value={loanForm.total_amount} onChange={(e) => setLoanForm({ ...loanForm, total_amount: e.target.value })} placeholder="1500000" className="mt-1" data-testid="loan-total-amount" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-blue-600">Online Amount</Label><Input type="number" value={loanForm.online_amount} onChange={(e) => handleOnlineChange(e.target.value)} className="mt-1" data-testid="loan-online-amount" /></div>
              <div><Label className="text-emerald-600">Deposit Amount</Label><Input type="number" value={loanForm.deposit_amount} onChange={(e) => setLoanForm({ ...loanForm, deposit_amount: e.target.value })} className="mt-1" data-testid="loan-deposit-amount" /></div>
            </div>
            {loanForm.total_amount && (
              <div className="text-xs text-slate-500">
                Online ({formatCurrency(parseFloat(loanForm.online_amount) || 0)}) + Deposit ({formatCurrency(parseFloat(loanForm.deposit_amount) || 0)}) = {formatCurrency((parseFloat(loanForm.online_amount) || 0) + (parseFloat(loanForm.deposit_amount) || 0))}
                {Math.abs((parseFloat(loanForm.online_amount) || 0) + (parseFloat(loanForm.deposit_amount) || 0) - (parseFloat(loanForm.total_amount) || 0)) > 0.01 && <span className="text-red-500 ml-1">(doesn't match total)</span>}
              </div>
            )}
            <div><Label>Notes (optional)</Label><Input value={loanForm.notes} onChange={(e) => setLoanForm({ ...loanForm, notes: e.target.value })} placeholder="e.g., Q1 working capital" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoanDialogOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600" onClick={handleSaveLoan} disabled={saving} data-testid="save-loan-btn">{saving ? 'Saving...' : editingLoan ? 'Update Loan' : 'Record Loan'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Surplus to Deposit</DialogTitle>
            <DialogDescription>
              Online surplus available: <strong>{formatCurrency(onlineSurplus)}</strong><br />
              <span className="text-xs">(Online Balance exceeds Funded amount by this much)</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Transfer Amount</Label><Input type="number" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} placeholder={`Max: ${Math.floor(onlineSurplus)}`} className="mt-1" data-testid="transfer-amount" /></div>
            <div><Label>Note (optional)</Label><Input value={transferNote} onChange={(e) => setTransferNote(e.target.value)} placeholder="e.g., Surplus from Q1 revenue" className="mt-1" /></div>
            {transferAmount && parseFloat(transferAmount) > onlineSurplus && (
              <p className="text-xs text-red-500">Amount exceeds available surplus ({formatCurrency(onlineSurplus)})</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button className="bg-amber-600" onClick={handleTransfer} disabled={transferring || (parseFloat(transferAmount) || 0) > onlineSurplus} data-testid="confirm-transfer-btn">{transferring ? 'Transferring...' : 'Transfer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repayment Dialog */}
      <Dialog open={repayOpen} onOpenChange={(open) => { setRepayOpen(open); if (!open) setEditingRepayment(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRepayment ? 'Edit Repayment' : 'Payout Loan (Repayment)'}</DialogTitle>
            <DialogDescription>{editingRepayment ? 'Update the repayment details' : 'Record a partial or full repayment to the funder. Effective loan will reduce.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Repayment Amount</Label><Input type="number" value={repayAmount} onChange={(e) => setRepayAmount(e.target.value)} placeholder="e.g., 500000" className="mt-1" data-testid="repay-amount" /></div>
            <div><Label>Repayment Date</Label><Input type="date" value={repayDate} onChange={(e) => setRepayDate(e.target.value)} className="mt-1" data-testid="repay-date" /></div>
            <div><Label>Note (optional)</Label><Input value={repayNote} onChange={(e) => setRepayNote(e.target.value)} placeholder="e.g., Q1 repayment" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRepayOpen(false); setEditingRepayment(null); }}>Cancel</Button>
            <Button className="bg-purple-600" onClick={handleRepay} disabled={repaying} data-testid="confirm-repay-btn">{repaying ? 'Saving...' : editingRepayment ? 'Update Repayment' : 'Record Repayment'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Transfer Dialog */}
      <Dialog open={editTransferOpen} onOpenChange={setEditTransferOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Transfer</DialogTitle>
            <DialogDescription>Update the transfer details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Transfer Amount</Label><Input type="number" value={editTransferAmount} onChange={(e) => setEditTransferAmount(e.target.value)} className="mt-1" data-testid="edit-transfer-amount" /></div>
            <div><Label>Note (optional)</Label><Input value={editTransferNote} onChange={(e) => setEditTransferNote(e.target.value)} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTransferOpen(false)}>Cancel</Button>
            <Button className="bg-amber-600" onClick={handleEditTransfer} disabled={editTransferSaving} data-testid="confirm-edit-transfer-btn">{editTransferSaving ? 'Saving...' : 'Update Transfer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Repayment Confirmation */}
      <AlertDialog open={deleteRepayOpen} onOpenChange={setDeleteRepayOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Repayment?</AlertDialogTitle><AlertDialogDescription>This will permanently remove this repayment record of {formatCurrency(repayToDelete?.amount)}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteRepayment} className="bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Transfer Confirmation */}
      <AlertDialog open={deleteTransferOpen} onOpenChange={setDeleteTransferOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Transfer?</AlertDialogTitle><AlertDialogDescription>This will permanently remove this transfer record of {formatCurrency(transferToDelete?.amount)}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteTransfer} className="bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Loan Record?</AlertDialogTitle><AlertDialogDescription>This will permanently remove this loan entry.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FunderPage;
